import { Link } from "react-router-dom";

import { useDashboard, useReadyForPickup } from "./api";
import { formatDate, daysLabel } from "./format";
import type { DashboardOrderCard, DashboardPayload } from "./types";

// Phase 4a customer-portal FR-3 — Dashboard with three sections:
// active rentals, upcoming reservations, past orders. Mobile-first
// stacked cards. Empty state for zero orders (FR-14).
export function DashboardPage(): JSX.Element {
  const q = useDashboard();
  if (q.isLoading) return <div className="p-4 text-sm text-zinc-600">Loading your dashboard…</div>;
  if (q.isError || !q.data) return <div className="p-4 text-sm text-red-700">Couldn&rsquo;t load — refresh.</div>;

  return <DashboardBody payload={q.data} />;
}

function DashboardBody({ payload }: { payload: DashboardPayload }): JSX.Element {
  const hasAny = payload.active.length + payload.upcoming.length + payload.past.length > 0;
  if (!hasAny) return <EmptyState />;

  return (
    <div className="space-y-6">
      <Section title={`Active rentals (${payload.active.length})`}>
        {payload.active.length === 0 ? (
          <Hint>No active rentals.</Hint>
        ) : (
          payload.active.map((o) => <ActiveCard key={o.order_number} order={o} disabled={payload.delinquent} />)
        )}
      </Section>

      <Section title={`Upcoming reservations (${payload.upcoming.length})`}>
        {payload.upcoming.length === 0 ? (
          <Hint>No upcoming reservations.</Hint>
        ) : (
          payload.upcoming.map((o) => <UpcomingCard key={o.order_number} order={o} />)
        )}
      </Section>

      <Section title="Past orders">
        {payload.past.length === 0 ? (
          <Hint>No past orders yet.</Hint>
        ) : (
          <ul className="divide-y divide-zinc-200 rounded border border-zinc-200">
            {payload.past.map((o) => (
              <li key={o.order_number} className="px-3 py-2 text-sm">
                <Link to={`/portal/orders/${o.order_number}`} className="underline">
                  #{o.order_number}
                </Link>{" "}
                · {o.equipment_summary} · {formatDate(o.delivery_date)}–{formatDate(o.pickup_date)} · {o.status}
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function ActiveCard({ order, disabled }: { order: DashboardOrderCard; disabled: boolean }): JSX.Element {
  const ready = useReadyForPickup(order.order_number);
  const alreadyPressed = order.pickup_notice_at != null || ready.data?.already_notified === true;

  return (
    <div className="rounded border border-zinc-200 p-3 space-y-2" data-testid={`active-${order.order_number}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Link to={`/portal/orders/${order.order_number}`} className="font-medium underline">
          #{order.order_number}
        </Link>
      </div>
      <div className="text-sm text-zinc-700">{order.equipment_summary}</div>
      <div className="text-xs text-zinc-600">
        Delivered {formatDate(order.delivery_date)} · Pickup {formatDate(order.pickup_date)}{" "}
        ({daysLabel(order.days_remaining)})
      </div>
      <div className="flex flex-wrap gap-2 pt-1">
        <button
          type="button"
          disabled={alreadyPressed || ready.isPending || disabled}
          onClick={() => ready.mutate()}
          className="min-h-[44px] rounded bg-zinc-900 px-3 py-2 text-sm text-white disabled:opacity-40"
          data-testid={`ready-${order.order_number}`}
        >
          {alreadyPressed ? "Pickup requested" : ready.isPending ? "Submitting…" : "Notify ready for pickup"}
        </button>
        <Link
          to={`/portal/orders/${order.order_number}#issue`}
          className="min-h-[44px] rounded border border-zinc-300 px-3 py-2 text-sm"
        >
          Report an issue
        </Link>
        {!disabled ? (
          <Link
            to={`/portal/reorder/${order.order_number}`}
            className="min-h-[44px] rounded border border-zinc-300 px-3 py-2 text-sm"
          >
            Reorder
          </Link>
        ) : null}
      </div>
    </div>
  );
}

function UpcomingCard({ order }: { order: DashboardOrderCard }): JSX.Element {
  return (
    <div className="rounded border border-zinc-200 p-3 text-sm space-y-1" data-testid={`upcoming-${order.order_number}`}>
      <Link to={`/portal/orders/${order.order_number}`} className="font-medium underline">
        #{order.order_number}
      </Link>
      <div className="text-zinc-700">{order.equipment_summary}</div>
      <div className="text-xs text-zinc-600">
        Delivery {formatDate(order.delivery_date)} · COI {order.coi_approved_at ? "Approved" : "Pending"}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <section>
      <h2 className="mb-2 text-base font-semibold">{title}</h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function Hint({ children }: { children: React.ReactNode }): JSX.Element {
  return <p className="text-sm text-zinc-500">{children}</p>;
}

function EmptyState(): JSX.Element {
  return (
    <div className="rounded border border-zinc-200 p-6 text-center" data-testid="dashboard-empty">
      <h2 className="text-lg font-semibold">No rentals yet</h2>
      <p className="mt-2 text-sm text-zinc-600">
        When you place your first reservation, it shows up here.
      </p>
      <Link
        to="/"
        className="mt-4 inline-block rounded bg-zinc-900 px-4 py-2 text-sm text-white"
      >
        Browse equipment
      </Link>
    </div>
  );
}
